<template>
<v-container class="pa-4">
  <v-row class="mb-4 align-center px-4 py-3 bg-light border-bottom rounded-t">
    <div class="d-flex align-center">
      <v-icon small class="mr-2" color="orange darken-2">fas fa-toolbox</v-icon>
      <span class="text-subtitle-2 font-weight-bold">Materiales e Insumos</span>
    </div>
    <v-spacer></v-spacer>
    <div v-if="!readonly">
      <v-btn 
        small 
        color="primary" 
        outlined 
        depressed 
        @click="addingSupply=true" 
        :disabled='disabled'
        class="mr-2"
      >
        <v-icon x-small class="mr-1">fas fa-plus</v-icon>
        Material
      </v-btn>
      <v-btn 
        small 
        color="primary" 
        outlined 
        depressed 
        @click="addingCustom=true" 
        :disabled='disabled'
      >
        <v-icon x-small class="mr-1">fas fa-magic</v-icon>
        Personalizado
      </v-btn>
    </div>
  </v-row>

  <v-simple-table v-if="items.length > 0" class="modern-table border rounded overflow-hidden">
    <template v-slot:default>
      <thead>
        <tr>
          <th width="40" class="text-center font-weight-bold grey--text">#</th>
          <th class="text-left font-weight-bold">Descripción</th>
          <th class="text-right font-weight-bold" v-if="$can('readCost', 'SalesNote')">Costo Unit.</th>
          <th width="100" class="text-center font-weight-bold">Cant. PP</th>
          <th width="100" class="text-left font-weight-bold">Unidad</th>
          <th class="text-right font-weight-bold" v-if="$can('readCost', 'SalesNote')">Costo Total</th>
          <th v-if="!readonly" width="60" class="text-center font-weight-bold"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(item, index) in items" :key="index">
          <td class="text-center caption grey--text">{{ index + 1 }}</td>
          <td>
            <div class="d-flex flex-column py-1">
              <span class="body-2 font-weight-medium text-left">{{item.name}}</span>
              <div v-if="item.has_dimension" class="d-flex align-center mt-1">
                <v-icon x-small color="info" class="mr-1">fas fa-ruler-combined</v-icon>
                <span class="caption info--text mr-2">{{item.width}} x {{item.height}} cm</span>
                <v-chip x-small color="info" outlined>Total: {{Math.ceil(item.qty * qty)}}</v-chip>
              </div>
            </div>
          </td>
          <td class="text-right" v-if="$can('readCost', 'SalesNote')">
            <span class="body-2">{{item.price | moneda}}</span>
          </td>
          <td class="px-2">
            <v-text-field
              v-if="!readonly"
              v-model="item.qty"
              dense
              outlined
              hide-details
              type="number"
              class="text-right centered-input"
              :disabled="disabled"
            ></v-text-field>
            <div v-else class="text-center font-weight-bold">
              {{item.qty >= 1 ? item.qty : item.qty.toFixed(3)}}
            </div>
          </td>
          <td>
            <v-chip x-small label outlined color="grey darken-1">{{item.unit_name}}</v-chip>
          </td>
          <td v-if="$can('readCost', 'SalesNote')" class="text-right">
            <div class="d-flex flex-column align-end">
              <span class="font-weight-bold">
                {{ calculateTotalCost(item) | moneda }}
              </span>
              <v-chip v-if="isUsingMinPrice(item)" x-small dark color="blue darken-1" class="mt-1">min.</v-chip>
              <v-progress-linear 
                :value="calculatePricePercentage(item)"
                color="primary"
                height="3"
                rounded
                class="mt-1"
                style="width: 60px"
              ></v-progress-linear>
            </div>
          </td>
          <td v-if="!readonly" class="text-center">
            <v-btn icon x-small color="error" @click="deleteItem(item)" :disabled='disabled'>
              <v-icon x-small>fas fa-trash-alt</v-icon>
            </v-btn>
          </td>
        </tr>
      </tbody>
    </template>
  </v-simple-table>

  <v-alert v-else type="info" text dense icon="fas fa-info-circle" class="mt-4 mx-4">
    No se han agregado materiales a este producto.
  </v-alert>

  <!-- Dialogs -->
  <v-dialog v-model="addingProcess" max-width="800px">
    <v-card class="pa-0 modern-card overflow-hidden">
      <v-sheet color="indigo darken-2" class="pa-6 d-flex align-center">
        <v-avatar size="44" color="white opacity-20" class="mr-4 shadow-sm">
          <v-icon color="white">fas fa-tasks</v-icon>
        </v-avatar>
        <div>
          <div class="caption white--text opacity-80 font-weight-black text-uppercase tracking-widest mb-1">Operaciones</div>
          <div class="text-h5 white--text font-weight-black">Agregar Proceso</div>
        </div>
        <v-spacer></v-spacer>
        <v-btn icon color="white" large @click="addingProcess = false">
          <v-icon>fas fa-times</v-icon>
        </v-btn>
      </v-sheet>
      <div class="pa-6">
        <add-cost @add="add" :is_supply="false"/>
      </div>
    </v-card>
  </v-dialog>

  <v-dialog v-model="addingSupply" max-width="800px">
    <v-card class="pa-0 modern-card overflow-hidden">
      <v-sheet color="primary" class="pa-6 d-flex align-center">
        <v-avatar size="44" color="white opacity-20" class="mr-4 shadow-sm">
          <v-icon color="white">fas fa-boxes</v-icon>
        </v-avatar>
        <div>
          <div class="caption white--text opacity-80 font-weight-black text-uppercase tracking-widest mb-1">Almacén</div>
          <div class="text-h5 white--text font-weight-black">Agregar Material</div>
        </div>
        <v-spacer></v-spacer>
        <v-btn icon color="white" large @click="addingSupply = false">
          <v-icon>fas fa-times</v-icon>
        </v-btn>
      </v-sheet>
      <div class="pa-6">
        <add-cost @add="add" :is_supply="true"/>
      </div>
    </v-card>
  </v-dialog>

  <v-dialog v-model="addingCustom" max-width="600px">
    <cost-create-update :cost="newCost" @save="add" @close="addingCustom=false" :custom="true" /> 
  </v-dialog>
</v-container>
</template>

<script>
import AddCost from '@/components/products/costs/AddCost'
import CostCreateUpdate from '@/components/products/costs/CostCreateUpdate'
import {calcSheets} from '@/utils/productCost'

export default {
  name: 'Supplies',
  components: { AddCost, CostCreateUpdate },
  props: ['items', 'qty', 'total', 'readonly', 'product_width', 'product_height', 'disabled'],
  data: () => ({
    addingProcess: false,
    addingSupply: false,
    addingCustom: false,
    newCost: {
      id: -1, unit: '', name: '', price: 0, min_price: 0, 
      is_supply: true, qty: 1, group: -1, has_dimension: false,
      width: 0, height: 0, has_article: false, price_update_option: 0
    },
  }),
  computed: {
    mts2() {
      return this.product_width * this.product_height * this.qty * 0.0001
    },
  },
  methods: {
    add(item) {
      if (this.items.includes(item)) {
        this.$set(item, 'qty', parseInt(item.qty) + 1)
      } else {
        this.$set(item, 'qty', 1)
        this.items.push(item)
        this.updateSupplySheetQty()
      }
      this.resetModalState()
    },
    resetModalState() {
      this.addingCustom = false
      this.addingProcess = false
      this.addingSupply = false
      this.newCost = {
        id: -1, unit: '', name: '', price: 0, min_price: 0, 
        is_supply: true, qty: 1, group: -1, has_dimension: false,
        width: 0, height: 0, has_article: false, price_update_option: 0
      }
    },
    deleteItem(item) {
      const index = this.items.indexOf(item)
      this.items.splice(index, 1)
    },
    updateSupplySheetQty() {
      this.items.filter(s => s.has_dimension).forEach(s => {
        s.qty = calcSheets({width: this.product_width, height: this.product_height, qty: this.qty}, s) / this.qty
      })
    },
    calculateTotalCost(item) {
      if (item.unit == 1) { // M2
        const calculated = item.price * this.mts2 * item.qty
        return item.min_price > calculated ? item.min_price : calculated
      } else if (item.unit == 2) { // Unit
        const calculatedQty = item.has_dimension ? Math.ceil(item.qty * this.qty) : (item.qty * this.qty)
        const calculated = item.price * calculatedQty
        return item.min_price > calculated ? item.min_price : calculated
      }
      return item.price * item.qty
    },
    isUsingMinPrice(item) {
      let calculated = 0
      if (item.unit == 1) calculated = item.price * this.mts2 * item.qty
      else if (item.unit == 2) calculated = item.price * (item.has_dimension ? Math.ceil(item.qty * this.qty) : (item.qty * this.qty))
      else return false
      return item.min_price > calculated
    },
    calculatePricePercentage(item) {
      const totalCost = this.calculateTotalCost(item)
      return (totalCost / (this.total || 1)) * 100
    }
  },
  watch: {
    product_width() { this.updateSupplySheetQty() },
    product_height() { this.updateSupplySheetQty() },
    qty() { this.updateSupplySheetQty() },
  }
}
</script>

<style scoped>
.bg-light { background-color: #f8fbfc; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.06); }
.border { border: 1px solid rgba(0,0,0,0.08) !important; }
.centered-input >>> input { text-align: right; }

.modern-table >>> td { height: 56px !important; border-bottom: 1px solid rgba(0,0,0,0.03) !important; font-size: 0.85rem !important; }
.modern-table >>> th { text-transform: uppercase; font-size: 0.65rem !important; font-weight: 800 !important; color: #64748b !important; letter-spacing: 1px !important; background-color: #f8fbfc !important; }
.tracking-widest { letter-spacing: 2px; }
.opacity-80 { opacity: 0.8; }
</style>